export type Lang = 'en' | 'ru'

/** New (2026) editions on Ridero. These replace every older store link. */
export const RIDERO = {
  vol1Ru: 'https://ridero.ru/books/azbuka_chelovecheskoi_psikhiki_1/',
  vol2Ru: 'https://ridero.ru/books/azbuka_chelovecheskoi_psikhiki_2_1/',
  vol1En: 'https://ridero.ru/books/alphabet_of_the_human_mind/',
  vol2En: 'https://ridero.ru/books/alphabet_of_the_human_mind_2/',
}

export interface BookVolume {
  volume: string
  title: string
  subtitle: string
  description: string
  pages: string
  isbn: string
  formats: string
  priceDigital: string
  pricePrint: string
  released: string
  url: string
  cover: string
  isNew: boolean
}

export interface Store {
  name: string
  url: string
  featured?: boolean
}

export interface Region {
  name: string
  flag: string
  stores: Store[]
}

/**
 * Причина стать психологом АЧП — в форме, которую умеет разложить вёрстка.
 *
 * `figure` и `sources` необязательны намеренно: восемь причин не равны друг
 * другу, и секция не должна делать вид, что равны. У первой есть число, которое
 * и решает («50%»), у третьей — перечисление источников трафика; у остальных ни
 * того, ни другого, и дорисовывать им украшение ради симметрии значит спрятать
 * то, что действительно весит.
 *
 * `alt` — не дубль заголовка, а ОПИСАНИЕ РИСУНКА. Панель со знаком практики
 * помечена `aria-hidden`, как это делает и сам референс: рисунок не читается
 * вслух по частям, он называется одной фразой. Без неё восемь состояний
 * существуют только для зрячих.
 */
export interface ProReason {
  /** Порядковый номер строкой: «01», а не 1 — он набирается как число на табло. */
  n: string
  title: string
  body: string
  /** Что показывает знак практики в этом состоянии — для тех, кто его не видит. */
  alt: string
  /** Одна величина, ради которой причину читают. */
  figure?: { value: string, caption: string }
  /** Перечисление, которое само по себе длиннее абзаца. */
  sources?: string[]
}

export const translations = {
  en: {
    // Accessible names for controls whose visible label is an icon or a
    // two-letter code — «RU» alone does not say what pressing it does. Said in
    // the language of the PAGE: whoever hears this one is reading in English.
    a11y: {
      switchLanguage: 'Switch to Russian',
    },

    // 404
    notFound: {
      title: 'This page does not exist',
      body: 'The link may be outdated, or the page has moved.',
      cta: 'Go to the homepage',
    },

    // Nav
    nav: {
      courses: 'Courses',
      books: 'Books',
      app: 'AHM App',
      certification: 'Certification',
      findPsychologist: 'Find a Psychologist',
      modeler: 'Scheme modeler',
      language: 'The scheme language',
      alphabet: 'The scheme vocabulary',
      classification: 'The AHM classification',
      about: 'About',
      cabinet: 'Cabinet',
      getStarted: 'Get Started',
    },

    // Announcement bar
    announce: {
      badge: 'New',
      text: 'Volume II "Global Psychotherapy", the revised 2026 edition, is out',
      cta: 'Get the book',
    },

    // News
    news: {
      label: 'News',
      date: 'July 17, 2026',
      title: 'Volume II arrives in its new edition',
      text: '"Alphabet of the Human Mind 2: Global Psychotherapy" has been fully revised by the author. 308 pages on charting conflicts before they happen. Digital from 800 ₽, print 1 170 ₽.',
      cta: 'Buy Volume II on Ridero',
      secondary: 'All editions',
    },

    // Hero
    hero: {
      // Первый экран говорит с тем же читателем, что и вся страница ниже: с
      // человеком, у которого дома молчание и ссоры на пустом месте, — а не с
      // психологом, выбирающим методологию. Курсы и сертификация никуда не
      // делись, они просто перестали быть первым, что он слышит.
      h1a: 'Manipulations ',
      h1accent: 'can be drawn',
      h1b: ' — and priced',
      lead:
        'AHM gives what happened a shape: who did what, in which order, and what it cost each of '
        + 'them — in percent of one day. Take your own case apart in five minutes, free and without '
        + 'an account.',
      ctaDraw: 'Take your case apart',
      ctaFind: 'Find an AHM psychologist',
      tagline: 'The Book Series That Forever Changed World Psychology',
      subtitle: 'Alphabet of the Human Mind (AHM) is a complete system for understanding human behavior, relationships, and the mind. Developed by Nikita & Ekaterina Danilov.',
      cta: 'Explore Courses',
      ctaSecondary: 'Browse Books',
      badge: 'Global Psychology Hub',
      trust: '10,000+ readers and learners worldwide',
    },

    // Stats
    stats: {
      readers: 'Readers Worldwide',
      psychologists: 'Certified Psychologists',
      countries: 'Countries',
      courses: 'Online Courses',
      followers: 'social media followers',
      countriesReached: 'countries reached',
      insights: 'AI insights generated',
    },

    // Courses
    courses: {
      title: 'AHM Courses',
      subtitle: 'Transform your understanding of the human mind with structured, expert-led programs',
      buyNow: 'Enroll Now',
      learnMore: 'Learn More',
      popular: 'Most Popular',
      new: 'New',
      items: [
        {
          id: 'kickstart',
          title: 'AHM Mini Course: Kickstart',
          description: 'Your first step into the Alphabet of the Human Mind. Learn the foundational concepts that underpin all AHM methodology, the key to understanding yourself and others.',
          price: '$97',
          originalPrice: '$197',
          duration: '4 hours',
          lessons: '12 lessons',
          level: 'Beginner',
          badge: 'Most Popular',
          highlights: ['Core AHM concepts', 'Self-assessment tools', 'Practical exercises', 'Certificate of completion'],
        },
        {
          id: 'relationships',
          title: 'AHM for Relationships',
          description: 'Deep dive into relationship dynamics through the AHM lens. Understand manipulation patterns, emotional bonds, and how to build healthier connections.',
          price: '$197',
          originalPrice: '$397',
          duration: '8 hours',
          lessons: '24 lessons',
          level: 'Intermediate',
          badge: null,
          highlights: ['Relationship mapping', 'Manipulation detection', 'Communication frameworks', 'Conflict resolution tools'],
        },
        {
          id: 'business',
          title: 'AHM for Business',
          description: 'Apply the AHM framework to organizational dynamics, leadership psychology, team performance, and business communication.',
          price: '$297',
          originalPrice: '$597',
          duration: '10 hours',
          lessons: '30 lessons',
          level: 'Intermediate',
          badge: null,
          highlights: ['Leadership psychology', 'Team dynamics analysis', 'Negotiation mastery', 'Organizational behavior'],
        },
        {
          id: 'hiring',
          title: 'AHM for Hiring',
          description: 'Revolutionary psychological screening for HR professionals and recruiters. Identify key personality traits, detect red flags, and build exceptional teams.',
          price: '$247',
          originalPrice: '$497',
          duration: '6 hours',
          lessons: '18 lessons',
          level: 'Intermediate',
          badge: 'New',
          highlights: ['Psychological screening', 'Interview frameworks', 'Red flag detection', 'Team compatibility analysis'],
        },
        {
          id: 'power',
          title: 'AHM & Power',
          description: 'Advanced program exploring power dynamics, authority psychology, and social influence through the AHM framework. For leaders, coaches, and professionals.',
          price: '$397',
          originalPrice: '$797',
          duration: '12 hours',
          lessons: '36 lessons',
          level: 'Advanced',
          badge: null,
          highlights: ['Power psychology', 'Social influence mechanisms', 'Authority & submission dynamics', 'Strategic thinking'],
        },
      ],
    },

    // Books
    books: {
      title: 'The AHM Book Series',
      subtitle: 'AHM is the first complete system for understanding a human being: not another typology, but a map of the psyche.',
      description: 'Written by Nikita and Ekaterina Danilov, these books form the theoretical and practical backbone of the entire AHM methodology. Both volumes were fully revised by the author in 2026.',
      buyGuide: 'Where to Buy',
      digital: 'Digital / E-Book',
      print: 'Print Edition',
      allRegions: 'Available in all regions',
      editionBadge: 'New Edition 2026',
      newBadge: 'New · July 17',
      buyCta: 'Buy on Ridero',
      buyVol2Cta: 'Buy Volume II on Ridero',
      heroNote: 'Digital from 800 ₽ · print available · epub, fb2, pdf, mobi',
      volumesTitle: 'Two volumes. One system.',
      volumesText: 'Volume I builds the map of the psyche. Volume II turns it into tactics: the most precise classification of personality types and a way to defuse conflicts before they start.',
      releasedLabel: 'Released',
      specPrompt: 'Want to see what the system is made of before you buy? The whole classification of both volumes — every term, its code and its definition — is published as a reference.',
      specLink: 'The AHM classification',
      pagesLabel: 'pages',
      quote: '"Every conflict can be charted, and defused."',
      quoteSource: 'Volume II · Global Psychotherapy',
      oldEditionsNote: 'Older editions are out of date. We recommend only the revised 2026 editions.',
      finalTitle: 'Read the editions the author stands behind',
      finalText: 'Both volumes, fully revised in 2026. Digital from 800 ₽ on Ridero.',
      contactPrompt: "Can't find the books in your region? ",
      contactLink: 'Contact us',
      bookList: ([
        {
          volume: 'Volume I',
          title: 'Alphabet of the Human Mind',
          subtitle: 'The Psychology Flagship',
          description: 'The series that forever changed world psychology. Not another typology, but a map of the psyche: every behavior has a cause, and every cause can be worked with.',
          pages: '436',
          isbn: '978-5-0070-0555-5',
          formats: 'epub · fb2 · pdf · mobi',
          priceDigital: '800 ₽',
          pricePrint: '1 359 ₽',
          released: 'May 11, 2026',
          url: RIDERO.vol1Ru,
          cover: '/book-cover-achp1.jpg',
          isNew: false,
        },
        {
          volume: 'Volume II',
          title: 'Alphabet of the Human Mind 2',
          subtitle: 'Global Psychotherapy',
          description: 'The most precise classification of personality types, and psychological tactics. Most "random" conflicts are rehearsed schemes. This book teaches you to chart them in advance and cut them off at a single point.',
          pages: '308',
          isbn: '978-5-0070-7783-5',
          formats: 'epub · fb2 · pdf · mobi',
          priceDigital: '800 ₽',
          pricePrint: '1 170 ₽',
          released: 'July 17, 2026',
          url: RIDERO.vol2Ru,
          cover: '/book-cover-achp2.jpg',
          isNew: true,
        },
      ] as BookVolume[]),
      regions: ([
        {
          name: 'Russia & CIS',
          flag: '🇷🇺',
          stores: [
            { name: 'Ridero · Vol. I, New Edition 2026', url: RIDERO.vol1Ru, featured: true },
            { name: 'Ridero · Vol. II, New Edition 2026', url: RIDERO.vol2Ru, featured: true },
          ],
        },
        {
          name: 'International / English',
          flag: '🌍',
          stores: [
            { name: 'Ridero · Vol. I (English)', url: RIDERO.vol1En },
            { name: 'Ridero · Vol. II (English)', url: RIDERO.vol2En },
          ],
        },
        {
          name: 'E-Book Formats',
          flag: '📱',
          stores: [
            { name: 'Vol. I · epub, fb2, pdf, mobi', url: RIDERO.vol1Ru },
            { name: 'Vol. II · epub, fb2, pdf, mobi', url: RIDERO.vol2Ru },
          ],
        },
      ] as Region[]),
    },

    // App
    app: {
      badge: 'Open beta',
      title: 'The AHM App',
      subtitle: 'The cabinet and PSY-Modeler are live in your browser. The modeler opens without an account — try it right now; an account is what keeps the schemes and runs the cabinet.',
      betaNote: 'Beta means exactly that: parts of the plan below are not built yet, and the parts that are may change between releases. Everything marked Live is what you can use today.',
      openCabinet: 'Open the cabinet',
      createAccount: 'Create an account',
      tryModeler: 'Try the modeler — no account',
      statusTitle: 'What works today',
      statusLive: 'Live',
      statusSoon: 'In progress',
      features: [
        {
          title: 'Manipulation Diagram',
          description: 'PSY-Modeler: build visual maps of manipulation patterns in your relationships, with five ready-made schemes as templates. Open without an account — a draft stays in your browser until you keep it.',
          icon: 'Network',
          status: 'live',
        },
        {
          title: 'Client Cabinet',
          description: 'People, circles of trust, groups, situations and dated observations with attachments. A psychological journal that stays on the server, not in one browser.',
          icon: 'FolderOpen',
          status: 'live',
        },
        {
          title: 'Accounts and recovery',
          description: 'Email registration with address confirmation, password recovery and password change. Changing a password ends every other session.',
          icon: 'ShieldCheck',
          status: 'live',
        },
        {
          title: 'AI Psychologist',
          description: 'Trained exclusively on AHM books by the authors themselves. Only recognizes what AHM defines as psychology, no contradictory theories. And when a question goes beyond an AI, the directory of psychologists is right there.',
          icon: 'Brain',
          status: 'soon',
        },
        {
          title: 'Psychologist Cabinet',
          description: 'A professional workspace for psychologists: clients, progress and work with colleagues.',
          icon: 'Users',
          status: 'soon',
        },
        {
          title: 'Team sessions and video',
          description: 'Many manipulation patterns require a team to overcome: several certified psychologists on one case, with video consultations.',
          icon: 'UsersRound',
          status: 'soon',
        },
      ],
      manipulationSection: {
        title: 'Why Manipulation Diagrams Change Everything',
        description: 'Most therapy fails because it ignores the manipulation ecosystem around a client. When someone heals a wound but manipulators are still actively attacking, the damage multiplies.',
        quote: 'It\'s like successfully treating a wound, only to have three new ones inflicted immediately after.',
        points: [
          'Identify all active manipulators in your life',
          'Map out manipulation patterns and triggers',
          'Create a structured plan to neutralize threats',
          'Track manipulation attempts over time',
        ],
      },
      steps: {
        label: 'Testing the beta',
        title: 'Four steps, about twenty minutes.',
        items: [
          {
            n: '01',
            title: 'Create an account',
            text: 'Email and password, then confirm the address from the letter.',
          },
          {
            n: '02',
            title: 'Fill the cabinet',
            text: 'Add a few real people, place them in circles of trust, write an observation.',
          },
          {
            n: '03',
            title: 'Chart a situation',
            text: 'Open PSY-Modeler from a situation and draw who directs which flow at whom.',
          },
          {
            n: '04',
            title: 'Tell us what broke',
            text: 'What you did, what you expected, what happened instead. Reply to the beta email.',
          },
        ],
      },
      beta: {
        label: 'Beta testing',
        title: 'Help us test the beta',
        lead: 'The beta is open — there is no queue and no invitation to wait for. Leave your address and we will send what is worth testing first and where to send what you find. You can also skip this and just create an account.',
        email: 'Email',
        name: 'Name',
        namePlaceholder: 'Optional',
        role: 'You are',
        roleReader: 'Reader of the books',
        rolePsychologist: 'Practising psychologist',
        roleOther: 'Someone else',
        note: 'What would you like to test?',
        notePlaceholder: 'Optional — a device, a language, a scenario you care about',
        submit: 'Sign up as a tester',
        sending: 'Sending…',
        successTitle: 'Check your inbox',
        successText: 'The instructions are on their way. The beta is open right now — create an account and start whenever you like.',
        failed: 'Could not send the application. Please try again.',
        tooMany: 'Too many attempts. Try again in a few minutes.',
        privacy: 'The address is used for beta letters only, and for nothing else.',
      },
    },

    // Certification
    certification: {
      title: 'AHM Psychologist Certification',
      subtitle: 'Become a globally recognized AHM-certified psychologist. Join a worldwide network of professionals working within the most advanced psychological framework.',
      disclaimer: 'The certification program has not launched yet: prices, splits and requirements listed here are preliminary and may change.',
      applyNow: 'Apply Now',
      levels: [
        {
          title: 'AHM Foundation Certificate',
          description: 'Entry-level certification for psychology professionals entering the AHM ecosystem.',
          duration: '3 months',
          price: '$599',
          earn: 'The first step toward certification income: conducting them opens at Level 2.',
          requirements: ['Psychology background', 'Completion of AHM Kickstart Course', 'Written assessment'],
        },
        {
          title: 'AHM Practitioner Certificate',
          description: 'Full practitioner certification for active therapists. Adds a Level 2 badge in the catalog and on your page, and priority in search results.',
          duration: '6 months',
          price: '$1,299',
          earn: 'Certify Foundation candidates — half the fee is yours: $299 per graduate.',
          requirements: ['Foundation Certificate', '50+ client sessions', 'Supervision sessions', 'Case study submission'],
        },
        {
          title: 'AHM Master Certificate',
          description: 'Advanced mastery for senior practitioners who train and supervise other AHM psychologists.',
          duration: '12 months',
          price: '$2,499',
          earn: 'Certify Foundation and Practitioner candidates — $299 or $649 per graduate.',
          requirements: ['Practitioner Certificate', '200+ sessions', 'Published case studies', 'Oral examination'],
        },
      ],
      payback: {
        title: 'Certification that pays back',
        lead: 'From Level 2 up, you conduct certifications for the levels below you — and keep 50% of their fee. Experience becomes income: the network grows by rewarding the people who grow it.',
        rows: [
          {who: 'Practitioner (Level 2)', what: 'certifies Foundation candidates', gets: '$299 per graduate'},
          {who: 'Master (Level 3)', what: 'certifies Foundation and Practitioner', gets: '$299 or $649 per graduate'},
        ],
        note: 'Certification sessions are booked through the AHM platform; the split is fixed at 50/50.',
      },
      benefits: [
        'Conduct lower-level certifications and keep 50% of the fee (from Level 2)',
        // Каталог и кабинет открыты каждому практикующему (решение владельца):
        // сертификат их не «открывает», а отмечает — значком, фильтром и местом.
        'Your level badge in the catalog and on your page',
        'Found by the certification-level filter, and ranked higher in search results',
        'Ongoing supervision and peer network',
        'Continuing education credits',
        'AHM branded materials and credentials',
      ],
    },

    // Find Psychologist
    findPsychologist: {
      title: 'Find an AHM Psychologist',
      subtitle: 'Practising psychologists worldwide. Those certified in AHM carry a badge with their level.',
      search: 'Search by name, location, or specialty...',
      filters: {
        country: 'Country',
        language: 'Language',
        specialty: 'Specialty',
        format: 'Format',
      },
      comingSoon: 'Directory Coming Soon',
      comingSoonDesc: 'We are currently onboarding our first cohort of certified AHM psychologists. Be the first to know when the directory launches.',
      notify: 'Notify Me',
    },

    // The playable schemes on the homepage — the shortest route from «what is
    // ACHP» to «that is my kitchen on that picture».
    demo: {
      label: 'See it on a scheme',
      title: 'Two ordinary stories, taken apart',
      lead:
        'Not a metaphor and not a test: the same objects the app draws — who did what, in which order, '
        + 'and what it cost each of them, in percent of one day of mental energy.',
      hint: 'Press play, or step through it beat by beat.',
      // Тот же интент, что у героя, — тот же лейбл (одна дверь = одно имя).
      ctaModeler: 'Take your case apart',
      ctaRegister: 'Create an account',
      note: 'The modeler opens without an account. An account is what makes a scheme stay — next to the people and situations it is about.',
    },

    // The scheme's whole vocabulary, drawn by the modeler itself — its own page.
    alphabet: {
      title: 'And this is the whole vocabulary',
      lead:
        'Everything the modeler draws is drawn by it right here on this page: the psychotype '
        + 'silhouettes, the kinds of flow, and the composite symptoms with what each one costs '
        + 'both sides. Nothing on a scheme is outside this list.',
      ctaModeler: 'Draw a scheme',
      ctaLanguage: 'The same language as text, for a person or an AI',
      ctaCatalog: 'Find a psychologist',
      nextTitle: 'A vocabulary is for using',
      nextBody:
        'The point of naming what happened is to see the price and the order of it. Draw your own '
        + 'case — the modeler is free and needs no account — or take the scheme to somebody who '
        + 'reads this language professionally.',
      teaser: 'Six silhouettes, nine kinds of flow, thirteen composite symptoms with a price for both sides.',
      teaserCta: 'The whole vocabulary',
    },

    // Финал повторял герой («смотреть курсы» / «книги») — страница
    // заканчивалась там же, где начиналась. Теперь она заканчивается тем, ради
    // чего написана, и кнопки под ним те же два действия.
    finalCta: {
      title: 'Your case is a scheme too',
      body:
        'Draw what happened and see what it cost each of them. The modeler is free and needs no '
        + 'account; when you are ready to take it apart with somebody, the catalog of psychologists '
        + 'is one click away — those certified in AHM carry a badge with their level.',
    },

    // The last door before the footer: draw something here, then pick where next.
    try: {
      title: 'Try it: build a scheme right here',
      lead:
        'Two clicks make a participant, a third draws a flow between them. Nothing is lost on the '
        + 'way — the modeler opens with exactly what you drew.',
      cta: 'Open the modeler',
      next: [
        {
          title: 'Find an AHM psychologist',
          body: 'Show the scheme to somebody who reads this language and works from it — verification is confirmed by us, never claimed by the profile.',
          cta: 'Open the catalog',
          href: '/find-psychologist',
        },
        {
          title: 'Get certified yourself',
          body: 'Three levels for practitioners: the badge, the rank in the catalog and the professional network behind it.',
          cta: 'How certification works',
          href: '/certification',
        },
        {
          title: 'The cabinet, when a sketch is not enough',
          body: 'People, circles of trust and situations — schemes stop being drawings and start being a case you return to.',
          cta: 'What the app does',
          href: '/app',
        },
      ],
    },

    // The catalog promo: a certified practitioner who already speaks this language.
    findPro: {
      label: 'Certified practitioners',
      title: 'Find a psychologist who already speaks this language',
      lead:
        'You do not have to explain the alphabet first. A psychologist who works with AHM reads the '
        + 'scheme you drew, names what is on it, and works from there. The certified ones carry a '
        + 'badge with their level.',
      points: [
        {
          title: 'The certificate is confirmed by us',
          body: 'Verification and the AHM level are written by moderation only — never by the profile itself.',
        },
        {
          title: 'The order is not «highest rated»',
          body: 'A directory sorted by rating buries every newcomer and pays for review fraud. Ours sorts by how complete and how certified a profile is.',
        },
        {
          title: 'Reviews come from sessions that happened',
          body: 'Only a completed session earns a voice, one review per pair, and it stays anonymous — including to the psychologist it is about.',
        },
      ],
      cta: 'Open the catalog',
      secondary: 'How certification works',
    },

    /**
     * Обращение к ПСИХОЛОГУ на странице, которая всю дорогу говорила с
     * читателем. Шесть причин, и порядок в них не случайный: сверху то, что
     * достаётся только первым, снизу то, что верно всегда.
     */
    becomePro: {
      chips: ['AHM diploma', 'Your name', 'New clients', 'Your workspace', 'Statistics', 'Reputation', 'Support', 'Community'],
      explore: 'A place for your practice',
      readAll: 'Read all 8 reasons',
      readOne: 'Explore one at a time',
      bookLabel: 'YOUR PRACTICE',
      title: 'Open your practice here — and be early',
      lead:
        'The catalog is still filling up. What the first ones take, nobody takes after them: '
        + 'the name, the place in the listing, the right to train whoever comes next.',

      /**
       * Порядок продуман, а не унаследован: сверху то, что достаётся ТОЛЬКО
       * первым (диплом, имя), в середине то, чем работают каждый день, в конце
       * — профессия целиком. Последнее состояние знака и есть «много
       * психологов», и оно закрывает секцию тем же доводом, которым она
       * открывалась: одному тут делать нечего, а первым — есть что.
       */
      reasons: [
        {
          n: '01',
          title: 'The AHM diploma, and the right to teach it',
          body:
            'Certification puts a level badge on your page and lifts it in search — and it does more: '
            + 'whoever earns it first trains the ones who come after, and keeps half of what that '
            + 'training is worth.',
          alt: 'The right to pass knowledge on',
          figure: { value: '50%', caption: 'of every course you run' },
        },
        {
          n: '02',
          title: 'Take your name while it is still free',
          body:
            'hub4psy.com/anna — short, one per platform, and yours for good. Names are never '
            + 'released back: a taken one stays taken even after its owner moves on.',
          alt: 'Your name on your practice',
        },
        {
          n: '03',
          title: 'Traffic you do not have to buy',
          body: 'The profile stands where people already looking for help walk past.',
          alt: 'Five paths to your profile',
          sources: ['AHM app users', 'SEO', 'GEO', 'AHM Marketing', 'AHM Blogs'],
        },
        {
          n: '04',
          title: 'A tool, not a form to fill in',
          body:
            'The modeler, the calendar, sessions, messages and a page of your own — built by people '
            + 'from Silicon Valley and run like a product somebody opens every day.',
          alt: 'Your working day, in one spread',
        },
        {
          n: '05',
          title: 'Open about what is actually happening',
          body:
            'How many consultations you have held and how alive the practice is are shown on your '
            + 'page as numbers the platform counts — never as a claim you make about yourself.',
          alt: 'A practice you can see in numbers',
        },
        {
          n: '06',
          title: 'A rating that is moderated, and open about it',
          body:
            'Nobody can buy their way above you, and nobody can drag you down with a review from '
            + 'a session that never happened. The platform holds the rule, not the psychologist '
            + 'with the largest marketing budget.',
          alt: 'A reputation grounded in facts',
        },
        {
          n: '07',
          title: 'Support out of Dubai and Silicon Valley',
          body:
            'Two of the world\u2019s technology capitals — and real people in both, who answer letters '
            + 'and fix whatever stands between you and your work.',
          alt: 'Support on both sides of the world',
        },
        {
          n: '08',
          title: 'Standardize psychology together',
          body:
            'A scheme you draw is read by another specialist with nothing explained first. That is '
            + 'what a shared language is, and it is built out of the practice of whoever arrives early.',
          alt: 'Different people. One professional language',
        },
      ] satisfies ProReason[],
      cta: 'Create a psychologist profile',
      secondary: 'How certification works',

      /**
       * Слова, которые попадают ВНУТРЬ рисунка. Отдельным блоком, потому что
       * это не подписи к интерфейсу: их набирает SVG, и переводить их надо
       * вместе с остальным текстом, а не оставлять зашитыми в фигуре.
       */
      mark: {
        handlePrefix: 'hub4psy.com/',
        handleName: 'anna',
        taken: ['anna.k', 'a.novak', 'annak'],
        cities: ['Dubai', 'Silicon Valley'],
      },

      /** Управление разворачиванием — только для скринридера и клавиатуры. */
      a11y: {
        region: 'Eight reasons to open a practice, with an illustration for each',
        close: 'Close and open the next reason',
        closeDetails: 'Close details',
        topics: 'Explore practice features',
        prev: 'Previous reason',
        next: 'Next reason',
      },
    },

    // About
    about: {
      title: 'About AHM',
      subtitle: 'The Alphabet of the Human Mind is more than a methodology. It is a new language for understanding the human psyche.',
      story: 'The AHM framework was developed over many years of research, clinical practice, and psychological observation by Nikita and Ekaterina Danilov. The result is a coherent, practical, and universal system for understanding human psychology that challenges many conventional assumptions.',
      mission: 'Our Mission',
      missionText: 'To make genuine psychological knowledge accessible worldwide, through books, courses, a global network of certified practitioners, and innovative AI-powered tools.',
      vision: 'Our Vision',
      visionText: 'A world where every person has access to accurate, manipulation-aware psychological support rooted in a scientifically sound and ethically coherent framework.',
      authors: {
        title: 'About the Authors',
        nikita: {
          name: 'Nikita Danilov',
          role: 'Co-author, AHM Series',
          bio: 'Researcher, psychologist, and co-creator of the Alphabet of the Human Mind framework. Nikita leads the theoretical development of AHM methodology and the AI Psychologist training program.',
        },
        ekaterina: {
          name: 'Ekaterina Danilova',
          role: 'Co-author, AHM Series',
          bio: 'Psychologist, author, and practitioner who co-developed the AHM framework. Ekaterina focuses on therapeutic applications, relationship psychology, and the global expansion of AHM.',
        },
      },
    },

    // Privacy Policy
    privacy: {
      title: 'Privacy Policy',
      lastUpdated: 'Effective: 23 August 2026',
      /** The «change your choice» link under the Analytics section. */
      changeConsent: 'Change your analytics choice',
      consentCleared: 'Your choice has been cleared — the banner is back at the bottom of the page.',
    },

    // Нижняя плашка согласия на аналитические cookie. Плашка, а не модалка:
    // сайт остаётся кликабельным, потому что человек пришёл читать про АЧП, а
    // не отвечать на вопрос про cookie.
    consent: {
      text: 'We count anonymous visits to see which pages help. Until you agree we run without cookies.',
      accept: 'Accept',
      necessary: 'Necessary only',
      more: 'Privacy Policy',
      /** Доступное имя всей плашки — для тех, кто слушает страницу, а не смотрит. */
      label: 'Analytics cookies',
    },

    // Terms
    terms: {
      title: 'Terms of Use',
      lastUpdated: 'Last Updated: September 26, 2026',
    },

    // Contact
    contact: {
      title: 'Contact Us',
      subtitle: 'Questions, partnerships, or media inquiries. We\'d love to hear from you.',
      form: {
        name: 'Your Name',
        email: 'Email Address',
        subject: 'Subject',
        message: 'Message',
        send: 'Send Message',
        success: 'Message sent! We\'ll be in touch soon.',
      },
      info: {
        email: 'hello@hub4psy.com',
        response: 'We typically respond within 24-48 hours',
      },
    },

    // Footer
    footer: {
      forAi: 'For AI agents and crawlers: the whole AHM classification is served as plain text, no markup and no JavaScript —',
      tagline: 'The Book Series That Forever Changed World Psychology',
      legal: {
        privacy: 'Privacy Policy',
        terms: 'Terms of Use',
        cookies: 'Cookie Policy',
      },
      copyright: '© 2026 AHM Global. All rights reserved.',
    },
  },

  ru: {
    // The label names the language you switch TO, so each locale carries the
    // OTHER one's wording — that is why this is English inside the ru block.
    a11y: {
      switchLanguage: 'Switch to English',
    },

    // 404
    notFound: {
      title: 'Такой страницы нет',
      body: 'Возможно, ссылка устарела или страница переехала.',
      cta: 'На главную',
    },

    nav: {
      courses: 'Курсы',
      books: 'Книги',
      app: 'Приложение АЧП',
      certification: 'Сертификация',
      findPsychologist: 'Найти психолога',
      modeler: 'Моделлер схем',
      language: 'Язык схем',
      alphabet: 'Словарь схемы',
      classification: 'Классификация АЧП',
      about: 'О нас',
      cabinet: 'Кабинет',
      getStarted: 'Начать',
    },

    announce: {
      badge: 'Новинка',
      text: 'Вышло новое издание второго тома — «Глобальная психотерапия»',
      cta: 'К книге',
    },

    news: {
      label: 'Новости',
      date: '17 июля 2026',
      title: 'Второй том вышел в новом издании',
      text: '«Азбука Человеческой Психики — 2. Глобальная психотерапия» полностью переработана автором. 308 страниц о том, как чертить конфликты до того, как они случаются. Электронная от 800 ₽, печатная 1 170 ₽.',
      cta: 'Купить Том II на Ridero',
      secondary: 'Все издания',
    },

    hero: {
      h1a: 'Манипуляции ',
      h1accent: 'можно нарисовать',
      h1b: ' — и увидеть их цену',
      lead:
        'АЧП даёт произошедшему форму: кто что сделал, в каком порядке и чего это стоило каждому — '
        + 'в процентах суточной нормы. Разберите свой случай за пять минут, бесплатно и без '
        + 'регистрации.',
      ctaDraw: 'Разобрать свой случай',
      ctaFind: 'Найти психолога АЧП',
      tagline: 'Серия книг, навсегда изменившая мировую психологию',
      subtitle: 'Азбука Человеческой Психики (АЧП) — полная система понимания человеческого поведения, отношений и разума. Разработана Никитой и Екатериной Даниловыми.',
      cta: 'Смотреть курсы',
      ctaSecondary: 'Книги',
      badge: 'Мировой центр психологии',
      trust: '10 000+ читателей и студентов по всему миру',
    },

    stats: {
      readers: 'Читателей по всему миру',
      psychologists: 'Сертифицированных психологов',
      countries: 'Стран',
      courses: 'Онлайн-курсов',
      followers: 'подписчиков в соцсетях',
      countriesReached: 'охваченных стран',
      insights: 'сгенерировано AI-инсайтов',
    },

    courses: {
      title: 'Курсы АЧП',
      subtitle: 'Трансформируйте понимание человеческой психики с помощью структурированных программ под руководством экспертов',
      buyNow: 'Записаться',
      learnMore: 'Подробнее',
      popular: 'Популярный',
      new: 'Новый',
      items: [
        {
          id: 'kickstart',
          title: 'Мини-курс по АЧП. Кик старт',
          description: 'Ваш первый шаг в мир Азбуки Человеческой Психики. Освойте базовые концепции методологии АЧП — ключ к пониманию себя и окружающих.',
          price: '7 900 ₽',
          originalPrice: '15 900 ₽',
          duration: '4 часа',
          lessons: '12 уроков',
          level: 'Начальный',
          badge: 'Популярный',
          highlights: ['Основы АЧП', 'Инструменты самоанализа', 'Практические задания', 'Сертификат'],
        },
        {
          id: 'relationships',
          title: 'АЧП для отношений',
          description: 'Глубокое погружение в динамику отношений через призму АЧП. Понять манипуляции, эмоциональные связи и как строить здоровые отношения.',
          price: '14 900 ₽',
          originalPrice: '29 900 ₽',
          duration: '8 часов',
          lessons: '24 урока',
          level: 'Средний',
          badge: null,
          highlights: ['Картирование отношений', 'Выявление манипуляций', 'Коммуникационные модели', 'Разрешение конфликтов'],
        },
        {
          id: 'business',
          title: 'АЧП для бизнеса',
          description: 'Применение АЧП в организационной динамике, психологии лидерства, эффективности команды и деловом общении.',
          price: '22 900 ₽',
          originalPrice: '45 900 ₽',
          duration: '10 часов',
          lessons: '30 уроков',
          level: 'Средний',
          badge: null,
          highlights: ['Психология лидерства', 'Анализ командной динамики', 'Мастерство переговоров', 'Организационное поведение'],
        },
        {
          id: 'hiring',
          title: 'АЧП для хайринга',
          description: 'Революционная психологическая оценка для HR-специалистов и рекрутеров. Выявляйте ключевые черты личности и формируйте исключительные команды.',
          price: '18 900 ₽',
          originalPrice: '37 900 ₽',
          duration: '6 часов',
          lessons: '18 уроков',
          level: 'Средний',
          badge: 'Новый',
          highlights: ['Психологический скрининг', 'Структуры собеседования', 'Выявление тревожных сигналов', 'Анализ совместимости команды'],
        },
        {
          id: 'power',
          title: 'АЧП и Власть',
          description: 'Продвинутая программа изучения динамики власти, психологии авторитета и социального влияния через методологию АЧП. Для лидеров, коучей и профессионалов.',
          price: '29 900 ₽',
          originalPrice: '59 900 ₽',
          duration: '12 часов',
          lessons: '36 уроков',
          level: 'Продвинутый',
          badge: null,
          highlights: ['Психология власти', 'Механизмы социального влияния', 'Динамика власти и подчинения', 'Стратегическое мышление'],
        },
      ],
    },

    books: {
      title: 'Серия книг АЧП',
      subtitle: 'АЧП — первая полная система понимания человека: не ещё одна типология, а карта психики.',
      description: 'Написанные Никитой и Екатериной Даниловыми, эти книги составляют теоретическую и практическую основу всей методологии АЧП. В 2026 году оба тома полностью переработаны автором.',
      buyGuide: 'Где купить',
      digital: 'Электронная версия',
      print: 'Печатное издание',
      allRegions: 'Доступно во всех регионах',
      editionBadge: 'Новое издание 2026',
      newBadge: 'Новинка · 17 июля',
      buyCta: 'Купить на Ridero',
      buyVol2Cta: 'Купить Том II на Ridero',
      heroNote: 'Электронная от 800 ₽ · есть печатная · epub, fb2, pdf, mobi',
      volumesTitle: 'Два тома. Одна система.',
      volumesText: 'Том I строит карту психики. Том II превращает её в тактику: самая точная классификация типов личности и способ обезвреживать конфликты до их начала.',
      releasedLabel: 'Дата выхода',
      specPrompt: 'Хотите увидеть, из чего система состоит, до покупки? Вся классификация обоих томов — каждый термин, его код и определение — опубликована справочником.',
      specLink: 'Классификация АЧП',
      pagesLabel: 'страниц',
      quote: '«Каждый конфликт можно начертить — и обезвредить»',
      quoteSource: 'Том II · Глобальная психотерапия',
      oldEditionsNote: 'Старые издания устарели. Рекомендуем только переработанные издания 2026 года.',
      finalTitle: 'Читайте издания, за которые отвечает автор',
      finalText: 'Оба тома полностью переработаны в 2026 году. Электронные от 800 ₽ на Ridero.',
      contactPrompt: 'Не можете найти книги в вашем регионе? ',
      contactLink: 'Свяжитесь с нами',
      bookList: ([
        {
          volume: 'Том I',
          title: 'Азбука Человеческой Психики',
          subtitle: 'Флагман психологии',
          description: 'Серия книг, навсегда изменившая мировую психологию. Не ещё одна типология, а карта психики: каждое поведение имеет причину, причина поддаётся работе.',
          pages: '436',
          isbn: '978-5-0070-0555-5',
          formats: 'epub · fb2 · pdf · mobi',
          priceDigital: '800 ₽',
          pricePrint: '1 359 ₽',
          released: '11 мая 2026',
          url: RIDERO.vol1Ru,
          cover: '/book-cover-achp1.jpg',
          isNew: false,
        },
        {
          volume: 'Том II',
          title: 'Азбука Человеческой Психики — 2',
          subtitle: 'Глобальная психотерапия',
          description: 'Самая точная классификация типов личности и психическая тактика. Большинство «случайных» конфликтов — отработанные схемы. Эта книга учит чертить их заранее и обрывать в одной точке.',
          pages: '308',
          isbn: '978-5-0070-7783-5',
          formats: 'epub · fb2 · pdf · mobi',
          priceDigital: '800 ₽',
          pricePrint: '1 170 ₽',
          released: '17 июля 2026',
          url: RIDERO.vol2Ru,
          cover: '/book-cover-achp2.jpg',
          isNew: true,
        },
      ] as BookVolume[]),
      regions: ([
        {
          name: 'Россия и СНГ',
          flag: '🇷🇺',
          stores: [
            { name: 'Ridero · Том I, новое издание 2026', url: RIDERO.vol1Ru, featured: true },
            { name: 'Ridero · Том II, новое издание 2026', url: RIDERO.vol2Ru, featured: true },
          ],
        },
        {
          name: 'Международный / English',
          flag: '🌍',
          stores: [
            { name: 'Ridero · Том I (English)', url: RIDERO.vol1En },
            { name: 'Ridero · Том II (English)', url: RIDERO.vol2En },
          ],
        },
        {
          name: 'Электронные форматы',
          flag: '📱',
          stores: [
            { name: 'Том I · epub, fb2, pdf, mobi', url: RIDERO.vol1Ru },
            { name: 'Том II · epub, fb2, pdf, mobi', url: RIDERO.vol2Ru },
          ],
        },
      ] as Region[]),
    },

    app: {
      badge: 'Открытая бета',
      title: 'Приложение АЧП',
      subtitle: 'Кабинет и PSY-Modeler уже работают в браузере. Моделлер открыт без регистрации — попробуйте прямо сейчас; аккаунт нужен, чтобы схемы сохранялись и работал кабинет.',
      betaNote: 'Бета — это именно бета: часть пунктов ниже ещё не построена, а построенное может меняться между обновлениями. Всё, что помечено «Работает», доступно уже сегодня.',
      openCabinet: 'Открыть кабинет',
      createAccount: 'Создать аккаунт',
      tryModeler: 'Попробовать моделлер без регистрации',
      statusTitle: 'Что уже работает',
      statusLive: 'Работает',
      statusSoon: 'В работе',
      features: [
        {
          title: 'Диаграмма манипуляций',
          description: 'PSY-Modeler: визуальные карты манипулятивных паттернов в ваших отношениях, пять готовых схем как шаблоны. Открыт без регистрации — черновик живёт в браузере, пока вы не сохраните его.',
          icon: 'Network',
          status: 'live',
        },
        {
          title: 'Кабинет клиента',
          description: 'Люди, круги доверия, группы, ситуации и датированные наблюдения с вложениями. Психологический журнал, который живёт на сервере, а не в одном браузере.',
          icon: 'FolderOpen',
          status: 'live',
        },
        {
          title: 'Аккаунт и восстановление',
          description: 'Регистрация по почте с подтверждением адреса, восстановление и смена пароля. Смена пароля завершает все остальные сессии.',
          icon: 'ShieldCheck',
          status: 'live',
        },
        {
          title: 'ИИ-психолог',
          description: 'Обучен исключительно по книгам АЧП лично авторами. Признаёт только то, что АЧП определяет как психологию — без противоречивых теорий. А когда вопрос выходит за пределы ИИ, рядом каталог психологов.',
          icon: 'Brain',
          status: 'soon',
        },
        {
          title: 'Кабинет психолога',
          description: 'Рабочее пространство психолога: клиенты, прогресс, совместная работа с коллегами.',
          icon: 'Users',
          status: 'soon',
        },
        {
          title: 'Командные сессии и видео',
          description: 'Многие схемы манипуляций сложно разрушить в одиночку: несколько сертифицированных психологов на одном случае и видеоконсультации.',
          icon: 'UsersRound',
          status: 'soon',
        },
      ],
      manipulationSection: {
        title: 'Почему диаграммы манипуляций меняют всё',
        description: 'Большинство видов терапии не дают результата, потому что игнорируют манипулятивную экосистему вокруг клиента. Когда человек исцеляется от раны, но манипуляторы продолжают активно атаковать — ущерб только умножается.',
        quote: 'Это как успешно вылечить рану, чтобы сразу после нанести три новых.',
        points: [
          'Определить всех активных манипуляторов в вашей жизни',
          'Составить карту манипулятивных паттернов и триггеров',
          'Разработать структурированный план нейтрализации угроз',
          'Отслеживать попытки манипуляции с течением времени',
        ],
      },
      steps: {
        label: 'Как тестировать бету',
        title: 'Четыре шага, минут двадцать.',
        items: [
          {
            n: '01',
            title: 'Завести аккаунт',
            text: 'Почта и пароль, затем подтверждение адреса по ссылке из письма.',
          },
          {
            n: '02',
            title: 'Наполнить кабинет',
            text: 'Добавьте несколько реальных людей, разложите по кругам доверия, запишите наблюдение.',
          },
          {
            n: '03',
            title: 'Разобрать ситуацию',
            text: 'Откройте PSY-Modeler из ситуации и нарисуйте, кто какой поток на кого направляет.',
          },
          {
            n: '04',
            title: 'Рассказать, что сломалось',
            text: 'Что делали, что ожидали, что получилось вместо этого. Ответом на письмо о бете.',
          },
        ],
      },
      beta: {
        label: 'Тестирование беты',
        title: 'Помогите протестировать бету',
        lead: 'Бета открыта — очереди и приглашений нет. Оставьте адрес, и мы пришлём, что проверить в первую очередь и куда присылать находки. Можно и не оставлять: просто заведите аккаунт.',
        email: 'Почта',
        name: 'Имя',
        namePlaceholder: 'Необязательно',
        role: 'Вы',
        roleReader: 'Читатель книг',
        rolePsychologist: 'Практикующий психолог',
        roleOther: 'Кто-то ещё',
        note: 'Что хотите проверить?',
        notePlaceholder: 'Необязательно — устройство, язык, важный для вас сценарий',
        submit: 'Стать бета-тестером',
        sending: 'Отправляем…',
        successTitle: 'Проверьте почту',
        successText: 'Инструкции уже летят к вам. Бета открыта прямо сейчас — заводите аккаунт и начинайте, когда удобно.',
        failed: 'Не удалось отправить заявку. Попробуйте ещё раз.',
        tooMany: 'Слишком много попыток. Повторите через несколько минут.',
        privacy: 'Адрес нужен только для писем о бете и ни для чего больше.',
      },
    },

    certification: {
      title: 'Сертификация психологов АЧП',
      subtitle: 'Станьте всемирно признанным сертифицированным психологом АЧП. Вступите в глобальную сеть профессионалов, работающих в рамках наиболее продвинутой психологической системы.',
      disclaimer: 'Программа сертификации ещё не запущена: цены, пропорции и требования на этой странице предварительные и могут измениться.',
      applyNow: 'Подать заявку',
      levels: [
        {
          title: 'Базовый сертификат АЧП',
          description: 'Начальный уровень сертификации для психологов, входящих в экосистему АЧП.',
          duration: '3 месяца',
          price: '49 900 ₽',
          earn: 'Первый шаг к доходу от сертификаций: проводить их можно со 2-го уровня.',
          requirements: ['Психологическое образование', 'Прохождение курса «Кик старт»', 'Письменная аттестация'],
        },
        {
          title: 'Сертификат практикующего специалиста АЧП',
          description: 'Полная сертификация для активных терапевтов. Значок 2-го уровня в каталоге и на вашей странице, приоритет в выдаче.',
          duration: '6 месяцев',
          price: '99 900 ₽',
          earn: 'Проводите базовые сертификации — половина стоимости ваша: 24 950 ₽ за каждого выпущенного.',
          requirements: ['Базовый сертификат', '50+ сессий с клиентами', 'Сессии супервизии', 'Разбор клинического случая'],
        },
        {
          title: 'Мастер-сертификат АЧП',
          description: 'Высший уровень мастерства для старших специалистов, обучающих и наблюдающих других АЧП-психологов.',
          duration: '12 месяцев',
          price: '189 900 ₽',
          earn: 'Проводите сертификации базового и практикующего уровней — 24 950 ₽ или 49 950 ₽ за выпуск.',
          requirements: ['Сертификат практикующего', '200+ сессий', 'Опубликованные клинические случаи', 'Устный экзамен'],
        },
      ],
      payback: {
        title: 'Сертификация, которая окупается',
        lead: 'Со второго уровня вы сами проводите сертификации нижних уровней — и получаете 50% их стоимости. Опыт становится доходом: сеть растёт, вознаграждая тех, кто её растит.',
        rows: [
          {who: 'Практикующий (уровень 2)', what: 'проводит базовые сертификации', gets: '24 950 ₽ за выпуск'},
          {who: 'Мастер (уровень 3)', what: 'проводит базовые и практикующие', gets: '24 950 ₽ или 49 950 ₽ за выпуск'},
        ],
        note: 'Сертификационные сессии проходят через платформу АЧП; доля фиксирована — 50/50.',
      },
      benefits: [
        'Проведение сертификаций нижних уровней — 50% их стоимости вам (со 2-го уровня)',
        'Значок вашего уровня в каталоге и на вашей странице',
        'Фильтр каталога по уровню сертификации и приоритет в выдаче',
        'Постоянная супервизия и профессиональная сеть',
        'Кредиты непрерывного образования',
        'Брендированные материалы и учётные данные АЧП',
      ],
    },

    findPsychologist: {
      title: 'Найти АЧП-психолога',
      subtitle: 'Практикующие психологи по всему миру. У сертифицированных по АЧП — значок с уровнем.',
      search: 'Поиск по имени, городу или специализации...',
      filters: {
        country: 'Страна',
        language: 'Язык',
        specialty: 'Специализация',
        format: 'Формат',
      },
      comingSoon: 'Каталог скоро появится',
      comingSoonDesc: 'Мы сейчас включаем первую группу сертифицированных АЧП-психологов. Будьте в числе первых, кто узнает о запуске каталога.',
      notify: 'Уведомить меня',
    },

    demo: {
      label: 'Посмотрите на схеме',
      title: 'Две обычные истории, разобранные по тактам',
      lead:
        'Не метафора и не тест: те же объекты, которые рисует приложение, — кто что сделал, в каком '
        + 'порядке и чего это стоило каждому, в процентах суточной нормы психической энергии.',
      hint: 'Нажмите «проиграть» или пройдите по тактам руками.',
      ctaModeler: 'Разобрать свой случай',
      ctaRegister: 'Завести кабинет',
      note: 'Моделлер открывается без регистрации. Кабинет нужен, чтобы схема осталась — рядом с людьми и ситуациями, о которых она.',
    },

    alphabet: {
      title: 'А это — весь словарь',
      lead:
        'Всё, что рисует моделлер, нарисовано им же прямо на этой странице: силуэты психотипов, '
        + 'виды потока и составные симптомы с ценой обеим сторонам. Ничего, кроме этого, на схеме '
        + 'не бывает.',
      ctaModeler: 'Нарисовать схему',
      ctaLanguage: 'Тот же язык текстом — человеку и ИИ',
      ctaCatalog: 'Найти психолога',
      nextTitle: 'Словарь нужен, чтобы им пользоваться',
      nextBody:
        'Назвать происходящее стоит ради того, чтобы увидеть его цену и порядок. Разберите свой '
        + 'случай — моделлер бесплатный и не требует регистрации — или отнесите схему тому, кто '
        + 'читает этот язык профессионально.',
      teaser: 'Шесть силуэтов, девять видов потока, тринадцать составных симптомов с ценой обеим сторонам.',
      teaserCta: 'Весь словарь',
    },

    finalCta: {
      title: 'Ваш случай — тоже схема',
      body:
        'Нарисуйте, что произошло, и посмотрите, чего это стоило каждому. Моделлер бесплатный и не '
        + 'требует регистрации; а когда захотите разобрать это с человеком — рядом каталог '
        + 'психологов, и у сертифицированных по АЧП стоит значок с уровнем.',
    },

    try: {
      title: 'Соберите схему прямо здесь',
      lead:
        'Два клика — двое участников, третий — поток между ними. По дороге ничего не теряется: '
        + 'моделлер откроется ровно с тем, что вы нарисовали.',
      cta: 'Открыть моделлер',
      next: [
        {
          title: 'Найти психолога АЧП',
          body: 'Показать схему тому, кто читает этот язык и работает дальше отсюда. Сертификат подтверждаем мы, а не сама анкета.',
          cta: 'Открыть каталог',
          href: '/find-psychologist',
        },
        {
          title: 'Пройти сертификацию',
          body: 'Три уровня для практикующих: значок, место повыше в выдаче и профессиональная сеть за ним.',
          cta: 'Как устроена сертификация',
          href: '/certification',
        },
        {
          title: 'Кабинет, когда наброска мало',
          body: 'Люди, круги доверия и ситуации — схемы перестают быть рисунками и становятся случаем, к которому возвращаются.',
          cta: 'Что умеет приложение',
          href: '/app',
        },
      ],
    },

    findPro: {
      label: 'Сертифицированные специалисты',
      title: 'Найдите психолога, который уже знает этот язык',
      lead:
        'Не придётся начинать с объяснения азбуки. Психолог, работающий с АЧП, читает нарисованную вами '
        + 'схему, называет то, что на ней, и работает дальше отсюда. У сертифицированных — значок с '
        + 'уровнем.',
      points: [
        {
          title: 'Сертификат подтверждаем мы',
          body: 'Верификацию и уровень АЧП проставляет только модерация — никогда не сама анкета.',
        },
        {
          title: 'Порядок — не «по рейтингу»',
          body: 'Каталог, отсортированный по оценкам, навсегда хоронит новичков и оплачивает накрутку. Наш сортирует по полноте анкеты и сертификации.',
        },
        {
          title: 'Отзывы — из состоявшихся сессий',
          body: 'Право голоса даёт только проведённая сессия, отзыв один на пару, и он анонимен — в том числе для того, о ком он.',
        },
      ],
      cta: 'Открыть каталог',
      secondary: 'Как устроена сертификация',
    },

    becomePro: {
      chips: ['Диплом АЧП', 'Ваше имя', 'Новые клиенты', 'Рабочее пространство', 'Статистика', 'Репутация', 'Поддержка', 'Сообщество'],
      explore: 'Место для вашей практики',
      readAll: 'Читать все 8 причин',
      readOne: 'Смотреть по одной',
      bookLabel: 'ВАША ПРАКТИКА',
      title: 'Открыть практику здесь — и успеть первым',
      lead:
        'Каталог только наполняется. Что достанется первым, после них не достанется никому: '
        + 'имя, место в выдаче, право учить следующих.',
      reasons: [
        {
          n: '01',
          title: 'Диплом АЧП и право обучать по нему',
          body:
            'Сертификация — это значок уровня на вашей странице и место выше в выдаче, но не только: '
            + 'кто получил её первым, тот обучает следующих — и оставляет себе половину стоимости '
            + 'этого обучения.',
          alt: 'Право передавать знания дальше',
          figure: { value: '50%', caption: 'от каждого курса, который вы проводите' },
        },
        {
          n: '02',
          title: 'Занять своё имя, пока оно свободно',
          body:
            'hub4psy.com/anna — короткий адрес, один на всю площадку и ваш навсегда. Имена не '
            + 'возвращаются в оборот: занятое остаётся занятым, даже если владелец ушёл на другое.',
          alt: 'Личное имя на обложке практики',
        },
        {
          n: '03',
          title: 'Трафик, который не нужно покупать',
          body: 'Профиль стоит там, где и так ходят люди, уже ищущие помощь.',
          alt: 'Пять путей к вашей странице',
          sources: ['Пользователи приложения АЧП', 'Поисковые системы', 'Ответы ИИ-ассистентов', 'Маркетинг AHM', 'Блоги AHM'],
        },
        {
          n: '04',
          title: 'Инструмент, а не анкета',
          body:
            'Моделлер, расписание, сессии, переписка и собственная страница — сделано командой из '
            + 'Кремниевой долины и работает как продукт, который открывают каждый день.',
          alt: 'Рабочий день на одном развороте',
        },
        {
          n: '05',
          title: 'Прозрачность: видно, что происходит',
          body:
            'Сколько консультаций проведено и насколько практика жива — показано на вашей странице '
            + 'числами, которые считает площадка, а не заявлением о самом себе.',
          alt: 'Практика, которую видно в числах',
        },
        {
          n: '06',
          title: 'Рейтинг, который модерируется и это видно',
          body:
            'Никто не купит место выше вашего и никто не утянет вас вниз отзывом за сессию, '
            + 'которой не было. Правило держит площадка, а не тот, у кого больше бюджет на '
            + 'продвижение.',
          alt: 'Репутация с проверкой фактов',
        },
        {
          n: '07',
          title: 'Поддержка из Дубая и Кремниевой долины',
          body:
            'Две технологические столицы мира — и живые люди в обеих, которые отвечают на письма '
            + 'и чинят всё, что стоит между вами и вашей работой.',
          alt: 'Поддержка по обе стороны мира',
        },
        {
          n: '08',
          title: 'Стандартизировать психологию вместе',
          body:
            'Нарисованную вами схему другой специалист читает, ничего не выясняя заранее. Это и '
            + 'есть общий язык — он складывается из практики тех, кто пришёл рано.',
          alt: 'Разные люди. Общий профессиональный язык',
        },
      ] satisfies ProReason[],
      cta: 'Создать профиль психолога',
      secondary: 'Как устроена сертификация',
      mark: {
        handlePrefix: 'hub4psy.com/',
        handleName: 'anna',
        taken: ['anna.k', 'a.novak', 'annak'],
        cities: ['Дубай', 'Кремниевая долина'],
      },
      a11y: {
        region: 'Восемь причин открыть практику, у каждой свой рисунок',
        close: 'Закрыть и открыть следующую причину',
        closeDetails: 'Закрыть подробности',
        topics: 'Возможности практики',
        prev: 'Предыдущая причина',
        next: 'Следующая причина',
      },
    },

    about: {
      title: 'Об АЧП',
      subtitle: 'Азбука Человеческой Психики — это не просто методология, это новый язык для понимания человеческой психики.',
      story: 'Система АЧП разрабатывалась на протяжении многих лет исследований, клинической практики и психологических наблюдений Никиты и Екатерины Даниловых. Результат — связная, практичная и универсальная система понимания человеческой психологии, бросающая вызов многим традиционным представлениям.',
      mission: 'Наша миссия',
      missionText: 'Сделать подлинные психологические знания доступными для всего мира — через книги, курсы, глобальную сеть сертифицированных специалистов и инновационные инструменты на основе ИИ.',
      vision: 'Наше видение',
      visionText: 'Мир, в котором каждый человек имеет доступ к точной, учитывающей манипуляции психологической поддержке, основанной на научно обоснованной и этически последовательной системе.',
      authors: {
        title: 'Об авторах',
        nikita: {
          name: 'Никита Данилов',
          role: 'Соавтор серии АЧП',
          bio: 'Исследователь, психолог и сооснователь системы Азбука Человеческой Психики. Никита руководит теоретическим развитием методологии АЧП и программой обучения ИИ-психолога.',
        },
        ekaterina: {
          name: 'Екатерина Данилова',
          role: 'Соавтор серии АЧП',
          bio: 'Психолог, автор и практик, совместно разработавшая систему АЧП. Екатерина сосредоточена на терапевтических применениях, психологии отношений и глобальном распространении АЧП.',
        },
      },
    },

    privacy: {
      title: 'Политика конфиденциальности',
      lastUpdated: 'Действует с 23 августа 2026 года',
      changeConsent: 'Изменить выбор об аналитике',
      consentCleared: 'Выбор сброшен — плашка снова внизу страницы.',
    },

    consent: {
      text: 'Мы считаем обезличенные визиты, чтобы понимать, какие страницы помогают. До вашего согласия — без cookie.',
      accept: 'Принять',
      necessary: 'Только необходимое',
      more: 'Политика конфиденциальности',
      label: 'Аналитические cookie',
    },

    terms: {
      title: 'Условия использования',
      lastUpdated: 'Последнее обновление: 26 сентября 2026',
    },

    contact: {
      title: 'Связаться с нами',
      subtitle: 'Вопросы, партнёрство или запросы СМИ — мы рады услышать вас.',
      form: {
        name: 'Ваше имя',
        email: 'Email',
        subject: 'Тема',
        message: 'Сообщение',
        send: 'Отправить',
        success: 'Сообщение отправлено! Мы скоро свяжемся с вами.',
      },
      info: {
        email: 'hello@hub4psy.com',
        response: 'Обычно мы отвечаем в течение 24–48 часов',
      },
    },

    footer: {
      forAi: 'Для ИИ-агентов и краулеров: вся классификация АЧП лежит плоским текстом, без разметки и без JavaScript —',
      tagline: 'Серия книг, навсегда изменившая мировую психологию',
      legal: {
        privacy: 'Политика конфиденциальности',
        terms: 'Условия использования',
        cookies: 'Политика cookies',
      },
      copyright: '© 2026 AHM Global. Все права защищены.',
    },
  },
}

export type Translations = typeof translations.en
